import React from 'react';
import { ArrowUpRight, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const S  = '#1C1917';
const SW = 1.5;

const canvasBg = {
  backgroundColor: '#F4F3F0',
  backgroundImage: 'radial-gradient(circle, #D0CEC9 1.2px, transparent 1.2px)',
  backgroundSize: '18px 18px',
};

/* ── Line art illustrations (static, always visible) ─────── */

const AnalyticsArt = () => (
  <svg viewBox="0 0 200 150" fill="none" className="w-full h-full p-6">
    {/* Axes */}
    <path d="M 25,10 L 25,125 L 185,125"
      stroke={S} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" />
    {/* Faint grid */}
    {[95, 68, 42].map((y, i) => (
      <path key={i} d={`M 28,${y} L 182,${y}`}
        stroke={S} strokeWidth={0.5} strokeOpacity="0.2" strokeDasharray="4 4" />
    ))}
    {/* Bar outlines */}
    {[{x:45,h:45},{x:75,h:72},{x:105,h:40},{x:135,h:88},{x:165,h:60}].map((b,i)=>(
      <rect key={i} x={b.x-10} y={125-b.h} width={20} height={b.h} rx={2}
        stroke={S} strokeWidth={SW} />
    ))}
    {/* Trend line */}
    <path d="M 45,80 C 60,58 90,55 105,85 C 118,42 130,37 165,65"
      stroke={S} strokeWidth={1} strokeLinecap="round" strokeDasharray="4 3" strokeOpacity="0.5" />
    {/* Trend dots */}
    {[[45,80],[105,85],[135,37],[165,65]].map(([x,y],i)=>(
      <circle key={i} cx={x} cy={y} r={3} stroke={S} strokeWidth={SW} fill="white" />
    ))}
  </svg>
);

const SystemArt = () => (
  <svg viewBox="0 0 200 160" fill="none" className="w-full h-full p-5">
    {/* 4 layer boxes */}
    {[12, 48, 84, 120].map((y, i) => (
      <g key={i}>
        <rect x="30" y={y} width="140" height="26" rx="4"
          stroke={S} strokeWidth={SW} />
        {/* Left indicator dot */}
        <circle cx="46" cy={y + 13} r="3" stroke={S} strokeWidth={1} fill="white" />
        {/* Label lines */}
        <path d={`M 58,${y+10} L 140,${y+10}`} stroke={S} strokeWidth={1} strokeOpacity="0.3" />
        <path d={`M 58,${y+17} L 110,${y+17}`} stroke={S} strokeWidth={1} strokeOpacity="0.2" />
        {/* Connector to next layer */}
        {i < 3 && (
          <path d={`M 100,${y+26} L 100,${y+38}`}
            stroke={S} strokeWidth={1} strokeOpacity="0.35" strokeDasharray="3 2" />
        )}
      </g>
    ))}
  </svg>
);

const MaturityArt = () => (
  <svg viewBox="0 0 200 150" fill="none" className="w-full h-full p-6">
    {/* Staircase path */}
    <path d="M 18,132 L 18,100 L 58,100 L 58,70 L 98,70 L 98,44 L 138,44 L 138,20 L 172,20"
      stroke={S} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" />
    {/* Step dots */}
    {[[18,100],[58,70],[98,44],[138,20]].map(([x,y],i)=>(
      <circle key={i} cx={x} cy={y} r={i===3?5:4}
        stroke={S} strokeWidth={SW} fill={i===3?S:'white'} />
    ))}
    {/* Faint diagonal guide */}
    <path d="M 18,132 L 172,20"
      stroke={S} strokeWidth={0.5} strokeOpacity="0.15" strokeDasharray="5 4" />
    {/* Baseline */}
    <path d="M 18,132 L 172,132"
      stroke={S} strokeWidth={1} strokeLinecap="round" strokeOpacity="0.3" />
    {/* Step labels */}
    {[[33,115],[73,85],[113,58],[153,33]].map(([x,y],i)=>(
      <text key={i} x={x} y={y} fontSize="8" fontFamily="monospace"
        fill={S} fillOpacity="0.4" textAnchor="middle">0{i+1}</text>
    ))}
  </svg>
);

/* ── Canvas panels ────────────────────────────────────────── */

const ImageCanvas = ({ src }) => (
  <div className="absolute inset-0" style={canvasBg}>
    <div className="absolute inset-3 rounded-xl overflow-hidden shadow-md bg-white flex items-center justify-center">
      <img src={src} className="w-full h-full object-contain p-4" alt="" />
    </div>
  </div>
);

const ArtCanvas = ({ children, bg }) => (
  <div className="absolute inset-0 flex items-center justify-center"
    style={{ ...canvasBg, ...(bg ? { backgroundColor: bg } : {}) }}>
    <div className="w-full h-full">
      {children}
    </div>
  </div>
);

/* ── Main Component ───────────────────────────────────────── */

const NarayanaWorkGrid = () => {
  const navigate = useNavigate();

  const projects = [
    {
      index: '01', name: 'Medha Scribe', tag: 'B2B · Clinical AI', url: 'https://www.medha-analytics.ai/medhascribe',
      headline: 'AI That Writes Notes While Doctors Heal',
      desc: 'Ambient AI documentation tool that removes the note-taking burden from physicians at the point of care.',
      impacts: ['50%+ time saved', '20+ hospitals', 'Voice-first UX', 'Trust-first AI design'],
      path: '/clinical-tool',
      canvas: <ImageCanvas src="https://customer-assets.emergentagent.com/job_design-clarity-3/artifacts/2mu7qy9b_Scribe.jpg" fit="object-top" />,
    },
    {
      index: '02', name: 'NH Care', tag: 'B2C · iOS + Android', url: 'https://play.google.com/store/apps/details?id=org.narayanahealth.nhcare&hl=en_IN',
      headline: 'One App for All Your Healthcare Needs',
      desc: 'Consultations, diagnostics, lab tests, and health records — unified in a single consumer experience.',
      impacts: ['4 modules redesigned', 'Faster doctor discovery', 'Design system shipped', 'IA restructured'],
      path: '/nh-care',
      canvas: <ImageCanvas src="https://customer-assets.emergentagent.com/job_design-clarity-3/artifacts/19v2frqv_NH%20Care.jpg" fit="object-center" />,
    },
    {
      index: '03', name: 'Analytics & Insights', tag: 'B2B · Data Platform', url: 'https://www.medha-analytics.ai/analytics',
      headline: 'Turning Clinical Data into Operational Intelligence',
      desc: 'Real-time dashboards that give hospital operations teams visibility into care delivery and performance.',
      impacts: ['+24% sessions', 'Funnel dashboards', 'Clinical ops visibility', 'Mixpanel adoption'],
      path: '/analytics',
      canvas: <ArtCanvas><AnalyticsArt /></ArtCanvas>,
    },
    {
      index: '04', name: 'Design System', tag: 'System Design · 0→1', url: null,
      headline: 'The Foundation Four AI Products Are Built On',
      desc: 'Tokens to templates — shared infrastructure across B2B and B2C, web and mobile.',
      impacts: ['4 AI products unified', '2 designers onboarded', 'AI interaction patterns', 'Tokens to templates'],
      path: '/foundations',
      canvas: <ArtCanvas bg="#F5F1EB"><SystemArt /></ArtCanvas>,
    },
    {
      index: '05', name: 'Design Maturity', tag: 'Leadership · Practice', url: null,
      headline: 'Transforming Design from Delivery to Strategy',
      desc: 'Built a data-informed, system-driven design practice — research ops, KPIs, team hiring, and design critiques.',
      impacts: ['6-step framework', 'KPIs on every brief', 'Research ops built', 'Team hired & managed'],
      path: '/maturity',
      canvas: <ArtCanvas bg="#EDEDEB"><MaturityArt /></ArtCanvas>,
    },
  ];

  return (
    <section className="pt-24 pb-24">
      <div className="container mx-auto px-6 md:px-12 max-w-6xl">

        {/* Section header */}
        <div className="mb-10">
          <div className="flex items-baseline justify-between mb-3">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-neutral-900 font-display">Narayana Health</h2>
              <span className="text-[9px] font-mono text-accent bg-accent/10 px-2 py-0.5 rounded-full uppercase tracking-wider">AI Division</span>
            </div>
            <span className="text-sm font-mono text-neutral-400">2021 – present</span>
          </div>
          <p className="text-sm text-neutral-500 leading-relaxed max-w-2xl">
            Joined as the founding designer of the AI Analytics Division — built the design function from zero,
            shipped 4 AI products across enterprise and consumer, established the design system, and grew the
            team from a solo practice into a structured, data-informed design org.
          </p>
        </div>

        {/* Project cards */}
        <div className="flex flex-col gap-3">
          {projects.map((project) => (
            <div
              key={project.index}
              className="flex border border-neutral-200 rounded-2xl overflow-hidden cursor-pointer group hover:border-neutral-300 hover:shadow-xl hover:shadow-neutral-900/[0.06] transition-[border-color,box-shadow] duration-300"
              onClick={() => navigate(project.path)}
            >
              {/* Left: label + canvas */}
              <div className="w-[300px] shrink-0 flex flex-col border-r border-neutral-100">
                <div className="px-5 pt-6 pb-2 flex items-center justify-between">
                  <span className="text-[10px] font-mono text-neutral-400 uppercase tracking-wider">{project.index}</span>
                  <span className="text-[10px] font-mono text-neutral-400">{project.tag}</span>
                </div>
                <div className="relative flex-1 mx-4 mb-4 rounded-xl overflow-hidden" style={{ minHeight: 170 }}>
                  {project.canvas}
                </div>
              </div>

              {/* Right: tag + headline + description + impacts */}
              <div className="flex-1 relative px-9 py-6 flex flex-col gap-2">
                {/* Arrow — absolute top-right */}
                <div className="absolute top-5 right-7 flex items-center gap-2">
                  {project.url && (
                    <a
                      href={project.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      className="flex items-center gap-1.5 text-[10px] font-mono text-neutral-500 bg-neutral-100 px-2.5 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-neutral-200 hover:text-neutral-900"
                    >
                      Live site <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  <div className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center group-hover:bg-neutral-900 transition-colors duration-200">
                    <ArrowUpRight className="w-4 h-4 text-neutral-500 group-hover:text-white" />
                  </div>
                </div>

                <span className="text-[10px] font-mono text-neutral-400 uppercase tracking-wider pr-12">
                  {project.name}
                </span>
                <h3 className="text-2xl font-display font-semibold text-neutral-900 leading-snug max-w-md pr-12" style={{ textWrap: 'balance' }}>
                  {project.headline}
                </h3>
                <p className="text-sm text-neutral-500 leading-relaxed max-w-sm mt-0.5">
                  {project.desc}
                </p>

                {/* Impact tags */}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {project.impacts.map(t => (
                    <span key={t} className="text-[10px] font-mono text-neutral-600 bg-neutral-100 px-2.5 py-1 rounded-full">
                      {t}
                    </span>
                  ))}
                </div>
              </div>

            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default NarayanaWorkGrid;
